import { useCallback, useEffect, useRef, useState } from "react";

const clamp = (value) => Math.max(0, Math.min(1, value));
const isMoving = (mode) => mode === "playing" || mode === "transition";

export default function useProductStoryPlayer({ product, controller, enhanced }) {
  const { active, claim, release, owns, register, visible, reducedMotion } = controller;
  const surface = `story:${product.id}`;
  const trackRef = useRef(null);
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const modeRef = useRef("scroll");
  const commandRef = useRef(null);
  const rafRef = useRef(0);
  const [mode, setMode] = useState("scroll");
  const [near, setNear] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [phaseIndex, setPhaseIndex] = useState(0);

  const changeMode = useCallback((next) => {
    modeRef.current = next;
    setMode(next);
  }, []);
  const cancelCommand = useCallback(() => {
    commandRef.current = null;
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
  }, []);
  const syncFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || !Number.isFinite(video.duration)) return;
    const time = video.currentTime;
    setProgress(clamp(time / Math.max(0.01, video.duration - 0.05)));
    setPhaseIndex(product.phases.reduce((index, phase, i) => time + 0.025 >= phase.start ? i : index, 0));
  }, [product.phases]);
  const stop = useCallback(() => {
    cancelCommand();
    videoRef.current?.pause();
    changeMode("paused");
    release(surface);
    syncFrame();
  }, [cancelCommand, changeMode, release, surface, syncFrame]);

  useEffect(() => {
    const video = videoRef.current;
    register(surface, video);
    return () => {
      cancelCommand();
      video.pause();
      register(surface, null);
      release(surface);
    };
  }, [cancelCommand, register, release, surface]);

  useEffect(() => {
    const preloadObserver = new IntersectionObserver(([entry]) => setNear(entry.isIntersecting), { rootMargin: "500px" });
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && isMoving(modeRef.current)) stop();
    });
    preloadObserver.observe(trackRef.current);
    visibilityObserver.observe(stageRef.current);
    return () => { preloadObserver.disconnect(); visibilityObserver.disconnect(); };
  }, [stop]);

  useEffect(() => { if (near && enhanced) setLoadVideo(true); }, [near, enhanced]);
  useEffect(() => {
    if (isMoving(modeRef.current) && (!visible || active !== surface)) stop();
  }, [active, visible, surface, stop]);

  // Commands own the timeline until explicitly returned to scroll mode.
  // Reverse playback uses decoded seeks: negative playbackRate is not portable.
  const startCommand = useCallback((kind, target = 0) => {
    cancelCommand();
    const video = videoRef.current;
    video.pause();
    if (!claim(surface, true)) return;
    if (video.error) {
      setFailed(false);
      setReady(false);
      video.load();
    }
    setLoadVideo(true);
    changeMode(kind === "play" ? "playing" : "transition");
    const command = { kind, target, initialized: false, elapsed: 0, last: 0 };
    commandRef.current = command;

    const tick = (now) => {
      rafRef.current = 0;
      if (commandRef.current !== command) return;
      if (!owns(surface) || document.hidden) { stop(); return; }
      const schedule = () => { rafRef.current = requestAnimationFrame(tick); };
      if (video.readyState < 2 || !Number.isFinite(video.duration)) { schedule(); return; }
      if (!command.initialized) {
        command.target = Math.max(0, Math.min(target, video.duration - 0.05));
        const ranges = video.seekable;
        if (!ranges.length || command.target > ranges.end(ranges.length - 1)) { schedule(); return; }
        command.initialized = true;
        command.from = video.currentTime;
        command.last = now;
        command.duration = Math.max(400, Math.abs(command.target - command.from) * 500);
        if (kind === "play") {
          video.currentTime = 0;
          video.play().then(() => {
            if (!owns(surface) || modeRef.current !== "playing") video.pause();
          }).catch(() => { if (commandRef.current === command) stop(); });
        }
      }
      if (kind === "play") {
        syncFrame();
        if (video.ended) { stop(); return; }
        schedule();
        return;
      }
      if (video.seeking || now - command.last < 1000 / 24) { schedule(); return; }
      // Follow elapsed time even when decoding is slow, so transitions do not
      // stretch into long waits on devices that cannot seek every frame.
      command.elapsed += now - command.last;
      command.last = now;
      const fraction = reducedMotion ? 1 : clamp(command.elapsed / command.duration);
      const eased = fraction * fraction * (3 - 2 * fraction);
      const time = command.from + (command.target - command.from) * eased;
      if (Math.abs(video.currentTime - time) > 0.001) video.currentTime = time;
      syncFrame();
      if (fraction === 1) {
        // Keep the last decoded frame and let seeked update the exact phase.
        stop();
        return;
      }
      schedule();
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [cancelCommand, claim, surface, changeMode, owns, stop, syncFrame, reducedMotion]);

  const togglePlay = useCallback(() => {
    if (isMoving(modeRef.current)) stop();
    else startCommand("play");
  }, [startCommand, stop]);
  const goToStep = useCallback((index) => {
    startCommand("transition", product.phases[index].start);
  }, [startCommand, product.phases]);
  const resumeScroll = useCallback(() => {
    cancelCommand();
    videoRef.current.pause();
    release(surface);
    changeMode("scroll");
  }, [cancelCommand, changeMode, release, surface]);

  useEffect(() => {
    if (!enhanced || !loadVideo || !duration || failed || mode !== "scroll" || !visible) return;
    const video = videoRef.current;
    let raf = 0;
    const update = () => {
      raf = 0;
      if (modeRef.current !== "scroll") return;
      const rect = trackRef.current.getBoundingClientRect();
      const stageHeight = stageRef.current.offsetHeight;
      const stickyTop = parseFloat(getComputedStyle(stageRef.current).top) || 104;
      if (rect.top >= window.innerHeight || rect.bottom <= stickyTop) { release(surface); return; }
      const next = clamp((stickyTop - rect.top) / Math.max(1, rect.height - stageHeight));
      const time = next * Math.max(0, duration - 0.05);
      // Only a visible, progressing story claims ownership; others cannot
      // interrupt a manually playing video just because they are preloaded.
      if (next > 0 && next < 1) {
        if (!claim(surface) || !owns(surface)) return;
      } else release(surface);
      const ranges = video.seekable;
      if (ranges.length && time <= ranges.end(ranges.length - 1) && !video.seeking && Math.abs(video.currentTime - time) > 0.035) video.currentTime = time;
      syncFrame();
    };
    const schedule = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    video.addEventListener("seeked", schedule);
    video.addEventListener("progress", schedule);
    video.addEventListener("canplay", schedule);
    schedule();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      video.removeEventListener("seeked", schedule);
      video.removeEventListener("progress", schedule);
      video.removeEventListener("canplay", schedule);
      // A click may already have claimed this same surface for manual playback.
      if (modeRef.current === "scroll") release(surface);
    };
  }, [enhanced, loadVideo, duration, failed, mode, visible, claim, owns, release, surface, syncFrame]);

  useEffect(() => {
    const video = videoRef.current;
    video.addEventListener("seeked", syncFrame);
    return () => video.removeEventListener("seeked", syncFrame);
  }, [syncFrame]);

  const onError = useCallback(() => {
    stop();
    setFailed(true);
    setReady(false);
  }, [stop]);
  const retry = useCallback(() => {
    startCommand("play");
  }, [startCommand]);

  return {
    mode, progress, phaseIndex, ready, failed, loadVideo, trackRef, stageRef, videoRef,
    togglePlay, goToStep, resumeScroll, stop, retry, onError,
    onMetadata: (event) => setDuration(event.currentTarget.duration),
    onLoadedData: () => { setReady(true); setFailed(false); },
    onTimeUpdate: syncFrame,
  };
}
